import {
  AIR_ACCEL,
  JUMP_STAMINA_COST,
  MAX_STAMINA,
  SKIP_MAX_SPEED_MULT,
  SKIP_SPEED_BONUS,
  SKIP_STAMINA_COST,
  SKIP_WINDOW_TICKS,
  SPRINT_DRAIN_PER_S,
  STAMINA_REGEN_TICKS,
  STAMINA_REGEN_PER_S,
  WINDED_RECOVER_AT,
  WINDED_SPEED_MULT,
  STEP_UP,
  FALL_SAFE_SPEED,
  FALL_DAMAGE_PER_MS,
  Stance,
  CROUCH_HEIGHT,
  STANCE_TICKS_CROUCH,
  CROUCH_SPEED,
  GRAVITY,
  GROUND_ACCEL,
  GROUND_FRICTION,
  JUMP_SPEED,
  MAX_HP,
  PLAYER_HEIGHT,
  PLAYER_RADIUS,
  SPRINT_SPEED,
  WALK_SPEED,
} from './constants.js';
import { clamp, type Vec3 } from './math.js';
import { heightAt, type Heightfield } from './terrain.js';

/**
 * The u16 button bitfield on `InputCmd`. See `docs/PROTOCOL-V3.md` §2 — that table is the
 * contract and these values are it.
 *
 * v3 freed five bits (Fire, Aim, Reload, Swap, Prone) and spent them on the ability bar. The bit
 * *values* moved, deliberately: a v2 client talking to a v3 server would fire abilities by
 * aiming, which is exactly what `PROTOCOL_VERSION` exists to prevent.
 */
export const Buttons = {
  Jump: 1 << 0,
  Sprint: 1 << 1,
  Crouch: 1 << 2,
  Interact: 1 << 3,
  Ability1: 1 << 4,
  Ability2: 1 << 5,
  Ability3: 1 << 6,
  Ability4: 1 << 7,
  Ability5: 1 << 8,
  Ability6: 1 << 9,
  /** A **toggle**: the rising edge flips auto-attack on or off. Not a hold. */
  AutoAttack: 1 << 10,
  /** Also a toggle. Any movement stands you back up. */
  Sit: 1 << 11,
} as const;

/**
 * Slot index (0..5) -> its `Buttons` bit, so nothing re-derives `1 << (4 + slot)` by hand.
 * `ABILITY_SLOTS` in constants.ts is its length.
 */
export const ABILITY_BUTTONS: readonly number[] = [
  Buttons.Ability1,
  Buttons.Ability2,
  Buttons.Ability3,
  Buttons.Ability4,
  Buttons.Ability5,
  Buttons.Ability6,
];

export interface InputCmd {
  seq: number; // client-side monotonically increasing
  dt: number; // seconds, clamped server-side
  moveX: number; // -1..1 strafe (right positive)
  moveY: number; // -1..1 forward positive
  /**
   * **Body** yaw, radians. Since v3 the camera has a yaw of its own that never crosses the wire:
   * the third-person camera can orbit a standing body, and the server has no business knowing
   * where you are looking. `stepPlayer`'s movement basis reads this, unchanged.
   */
  yaw: number;
  buttons: number; // Buttons bitfield
  /**
   * The player's selected target (a player id or an entity id), or 0.
   *
   * It rides the input stream rather than a message of its own because it changes at input rate,
   * it must be in force for the ability pressed in the *same* command, and a separate message
   * would arrive on a different tick from the button that needed it. It is a **request**: the
   * server re-validates it every tick (exists, alive, within `TARGET_KEEP_RANGE`) and clears it
   * otherwise. Never trusted as a fact.
   */
  targetId: number;
}

export interface PlayerState {
  id: number;
  pos: Vec3;
  vel: Vec3;
  /** Body yaw, radians. The camera's yaw is the client's own business — see `InputCmd.yaw`. */
  yaw: number;
  hp: number;
  grounded: boolean;
  crouching: boolean;
  lastInputSeq: number;
  /** 0..MAX_STAMINA. Sprint and jumps drain it; regenerates after a short delay. */
  stamina: number;
  /** Ticks remaining before stamina regen resumes (integer, wire-exact). */
  regenTicks: number;
  /** True after stamina hit zero, until it recovers past WINDED_RECOVER_AT. */
  winded: boolean;
  /** Ticks spent grounded since the last landing (saturates at 255). */
  groundTicks: number;
  /** Fall damage applied by the most recent step (0 when none); the server turns it into events. */
  fallDamage: number;
  /** Feet height when the player last left the ground; mantle reach is measured from here. */
  airBaseY: number;
  /**
   * Per-player modifiers set by the server from Calling passives / skills and mirrored to the
   * client through the snapshot so prediction uses identical numbers. speedMult is a fraction
   * multiplier (1 = base); staminaMax replaces MAX_STAMINA as the cap.
   */
  speedMult: number;
  staminaMax: number;
  /** Stance (constants.Stance). `crouching` mirrors stance === Crouch for older code paths. */
  stance: number;
  /** Ticks left in a stance transition (movement frozen). */
  stanceTicks: number;
}

export const newPlayerState = (id: number, spawn: Vec3): PlayerState => ({
  id,
  pos: { ...spawn },
  vel: { x: 0, y: 0, z: 0 },
  yaw: 0,
  hp: MAX_HP,
  grounded: false,
  crouching: false,
  lastInputSeq: 0,
  stamina: MAX_STAMINA,
  regenTicks: 0,
  winded: false,
  groundTicks: 0,
  fallDamage: 0,
  airBaseY: 0,
  stance: Stance.Stand,
  stanceTicks: 0,
  speedMult: 1,
  staminaMax: MAX_STAMINA,
});

/** Axis-aligned box collider used for the M0 greybox world. */
export interface Aabb {
  min: Vec3;
  max: Vec3;
}

export interface World {
  boxes: Aabb[];
  /** Flat ground level used where there is no terrain. */
  groundY: number;
  /** Optional heightfield; when present the ground is sampled from it instead of groundY. */
  terrain?: Heightfield;
}

/** Ground height under (x, z): terrain if present, else the flat groundY. */
export function groundHeight(world: World, x: number, z: number): number {
  return world.terrain ? heightAt(world.terrain, x, z) : world.groundY;
}

/** How far the feet may drop (or rise) in one tick and still count as standing on it. */
const STEP_DOWN = 0.6;

/**
 * The highest surface under a standing player's feet: the terrain, or the top of any box the
 * capsule footprint overlaps whose top is no higher than a step above the feet. Boxes well
 * below the feet do not count — that is a fall, not a floor.
 */
export function supportHeight(world: World, x: number, z: number, y: number): number {
  let best = groundHeight(world, x, z);
  const r = PLAYER_RADIUS;
  for (const b of world.boxes) {
    if (x + r <= b.min.x || x - r >= b.max.x || z + r <= b.min.z || z - r >= b.max.z) continue;
    const top = b.max.y;
    // At or just below the feet only: a box top above the feet is a riser to mantle, never a
    // surface to be snapped up onto (that would teleport the capsule into whatever it abuts).
    if (top > y + 0.05 || top < y - STEP_DOWN) continue;
    if (top > best) best = top;
  }
  return best;
}

/**
 * Deterministic kinematic step shared by client (prediction) and server (authority).
 * Separate-axis resolution against world boxes; good enough for M0 and replaced by a
 * Rapier character controller in M1 behind the same signature.
 */
export function stepPlayer(p: PlayerState, cmd: InputCmd, world: World): void {
  const dt = clamp(cmd.dt, 0, 0.1);
  if (dt === 0) return;

  p.yaw = cmd.yaw;
  // --- stance ----------------------------------------------------------------------------
  // Two stances since v3. Prone went with the shooter it belonged to: a tab-target MMO has no
  // use for it, and its 18-tick transition was the single worst thing about moving in this game.
  const wantStance = cmd.buttons & Buttons.Crouch ? Stance.Crouch : Stance.Stand;
  if (p.stanceTicks > 0) p.stanceTicks -= 1;
  if (wantStance !== p.stance && p.stanceTicks === 0) {
    // Standing up needs head room; stay low under a ceiling.
    const targetH = wantStance === Stance.Crouch ? CROUCH_HEIGHT : PLAYER_HEIGHT;
    const currentH = p.stance === Stance.Crouch ? CROUCH_HEIGHT : PLAYER_HEIGHT;
    let blocked = false;
    if (targetH > currentH) {
      for (const b of world.boxes) {
        if (overlaps(p.pos, targetH, b)) {
          blocked = true;
          break;
        }
      }
    }
    if (!blocked) {
      p.stanceTicks = STANCE_TICKS_CROUCH;
      p.stance = wantStance;
    }
  }
  p.crouching = p.stance === Stance.Crouch;
  const transitioning = p.stanceTicks > 0;

  const mx = clamp(cmd.moveX, -1, 1);
  const my = clamp(cmd.moveY, -1, 1);
  const mlen = Math.hypot(mx, my);
  const nx = mlen > 1 ? mx / mlen : mx;
  const ny = mlen > 1 ? my / mlen : my;
  const sin = Math.sin(p.yaw);
  const cos = Math.cos(p.yaw);
  // yaw = 0 faces -Z (three.js convention); positive yaw turns left.
  const wishX = nx * cos - ny * sin;
  const wishZ = -nx * sin - ny * cos;
  // --- stamina ---------------------------------------------------------------------------
  const wantsSprint = (cmd.buttons & Buttons.Sprint) !== 0 && ny > 0.1 && !p.crouching;
  const sprinting = wantsSprint && p.stamina > 0 && !p.winded;
  if (sprinting) {
    p.stamina = Math.max(0, p.stamina - SPRINT_DRAIN_PER_S * dt);
    p.regenTicks = STAMINA_REGEN_TICKS;
  }
  let speed = p.crouching ? CROUCH_SPEED : sprinting ? SPRINT_SPEED : WALK_SPEED;
  if (p.winded) speed *= WINDED_SPEED_MULT;
  speed *= p.speedMult;
  if (transitioning) speed = 0;

  const accel = p.grounded ? GROUND_ACCEL : AIR_ACCEL;
  let tx = wishX * speed;
  let tz = wishZ * speed;
  const inSkipWindow = p.grounded && p.groundTicks <= SKIP_WINDOW_TICKS;
  const hasInput = nx !== 0 || ny !== 0;
  const hspeed = Math.hypot(p.vel.x, p.vel.z);
  // Ground friction only when the player is not driving (or when driving faster than the
  // target after a skip window, so speed bleeds back to sprint). Applying friction while
  // driving would cap speed below the target (friction and accel would fight each tick).
  if (p.grounded && !inSkipWindow && !hasInput) {
    const f = Math.max(0, 1 - GROUND_FRICTION * dt);
    p.vel.x *= f;
    p.vel.z *= f;
  }
  // In the air (and in the landing grace window) never decelerate below the current speed:
  // steer the velocity toward the wish direction while preserving magnitude, Quake-style.
  if ((!p.grounded || inSkipWindow) && hspeed > speed && hasInput) {
    tx = wishX * hspeed;
    tz = wishZ * hspeed;
  }
  p.vel.x += clamp(tx - p.vel.x, -accel * dt, accel * dt);
  p.vel.z += clamp(tz - p.vel.z, -accel * dt, accel * dt);

  if (p.grounded && cmd.buttons & Buttons.Jump && !transitioning) {
    const skip =
      inSkipWindow && hspeed >= WALK_SPEED * 0.9 && p.stamina >= SKIP_STAMINA_COST && !p.winded;
    if (skip) {
      const cap = SPRINT_SPEED * SKIP_MAX_SPEED_MULT;
      const boosted = Math.min(cap, hspeed * SKIP_SPEED_BONUS);
      const k = hspeed > 0 ? boosted / hspeed : 1;
      p.vel.x *= k;
      p.vel.z *= k;
      p.stamina = Math.max(0, p.stamina - SKIP_STAMINA_COST);
    } else {
      p.stamina = Math.max(0, p.stamina - JUMP_STAMINA_COST);
    }
    p.regenTicks = STAMINA_REGEN_TICKS;
    p.vel.y = JUMP_SPEED;
    p.grounded = false;
  }
  if (p.stamina <= 0) p.winded = true;
  if (p.winded && p.stamina >= WINDED_RECOVER_AT) p.winded = false;
  if (p.regenTicks > 0) p.regenTicks -= 1;
  else if (!sprinting) p.stamina = Math.min(p.staminaMax, p.stamina + STAMINA_REGEN_PER_S * dt);
  // Stamina stays on a 0.01 grid, deliberately finer than the u8 the wire now carries.
  //
  // It cannot be rounded to whole units here: sprint drains 10/s, which is a third of a unit per
  // 30 Hz tick, and rounding each tick would round it to zero and make sprinting free forever.
  // So the sim keeps two decimal places and the snapshot sends the rounded whole number, which
  // is all a remote body's bar needs — and the local player predicts its own from this float
  // rather than reading the byte back (see `docs/PROTOCOL-V3.md` §3.1).
  p.stamina = Math.round(p.stamina * 100) / 100;
  p.vel.y += GRAVITY * dt;

  const h = p.crouching ? CROUCH_HEIGHT : PLAYER_HEIGHT;
  const wasGrounded = p.grounded;
  const vyBefore = p.vel.y;

  moveAxis(p, 'x', p.vel.x * dt, h, world);
  moveAxis(p, 'z', p.vel.z * dt, h, world);
  if (!p.grounded) {
    p.grounded = false;
    moveAxis(p, 'y', p.vel.y * dt, h, world);
    const gy = groundHeight(world, p.pos.x, p.pos.z);
    if (p.pos.y <= gy) {
      p.pos.y = gy;
      if (p.vel.y < 0) p.vel.y = 0;
      p.grounded = true;
    }
  } else {
    // Grounded: follow whatever is actually under the feet — the terrain (slopes) or a box top
    // (roofs, decks, crates) — and let go when nothing is within a step. Box tops count as
    // support, so a roof no longer flips grounded every tick, and a world without terrain no
    // longer lets a player walk off a crate and keep standing on air (2026-09-04 playtest:
    // "fall damage does not work" — the walk-off never became a fall).
    const sup = supportHeight(world, p.pos.x, p.pos.z, p.pos.y);
    if (sup >= p.pos.y - STEP_DOWN && sup <= p.pos.y + STEP_DOWN) {
      p.pos.y = sup;
      // Standing: gravity has nothing to accumulate into. Without this a long stand banked
      // metres per second of "fall" that fired the instant a player stepped off a kerb.
      if (p.vel.y < 0) p.vel.y = 0;
    } else if (sup < p.pos.y - STEP_DOWN) {
      p.grounded = false; // walked off an edge: a real fall starts from rest
      if (p.vel.y < 0) p.vel.y = 0;
    }
  }
  // Fall damage on the landing tick, from the pre-impact vertical speed.
  p.fallDamage = 0;
  if (!wasGrounded && p.grounded && vyBefore < -FALL_SAFE_SPEED) {
    p.fallDamage = Math.min(p.hp, Math.round((-vyBefore - FALL_SAFE_SPEED) * FALL_DAMAGE_PER_MS));
    p.hp = Math.max(0, p.hp - p.fallDamage);
  }
  // Refreshed on every grounded tick; the fall-damage baseline is measured from it.
  if (p.grounded) p.airBaseY = p.pos.y;
  p.groundTicks = p.grounded ? Math.min(255, p.groundTicks + 1) : 0;
  p.lastInputSeq = cmd.seq;
}

/**
 * The auto step-up: a grounded player walking into a box whose top is no more than `STEP_UP`
 * above the feet rides up onto it instead of being stopped dead. Without this, `moveAxis`'s
 * per-axis push-out makes any authored riser an impassable wall (the terrain heightfield has
 * always auto-stepped a full `STEP_DOWN`, so only *boxes* were affected). Since v3 removed
 * mantling this is the ONLY way up a step: anything taller than `STEP_UP` is a wall, and a map
 * that wants a player on top of it has to author stairs.
 *
 * Returns true when the climb happened, in which case the caller must NOT zero the velocity on
 * that axis.
 */
function stepUpOnto(p: PlayerState, b: Aabb, h: number, world: World): boolean {
  if (!p.grounded) return false;
  const top = b.max.y;
  const rise = top - p.pos.y;
  if (rise <= 0 || rise > STEP_UP) return false;
  // Head room for the *current* stance at the stepped-up height, at the position we would
  // occupy: a crouching player must not stand up through a ceiling to take a step.
  const probe = { x: p.pos.x, y: top, z: p.pos.z };
  for (const o of world.boxes) if (overlaps(probe, h, o)) return false;
  p.pos.y = top;
  return true;
}

function overlaps(p: Vec3, h: number, b: Aabb): boolean {
  return (
    p.x + PLAYER_RADIUS > b.min.x &&
    p.x - PLAYER_RADIUS < b.max.x &&
    p.y + h > b.min.y &&
    p.y < b.max.y &&
    p.z + PLAYER_RADIUS > b.min.z &&
    p.z - PLAYER_RADIUS < b.max.z
  );
}

function moveAxis(p: PlayerState, axis: 'x' | 'y' | 'z', delta: number, h: number, world: World) {
  if (delta === 0) return;
  p.pos[axis] += delta;
  for (const b of world.boxes) {
    if (!overlaps(p.pos, h, b)) continue;
    if (axis === 'y') {
      if (delta < 0) {
        p.pos.y = b.max.y;
        p.vel.y = 0;
        p.grounded = true;
      } else {
        p.pos.y = b.min.y - h;
        p.vel.y = 0;
      }
    } else if (axis === 'x') {
      if (stepUpOnto(p, b, h, world)) continue;
      p.pos.x = delta > 0 ? b.min.x - PLAYER_RADIUS : b.max.x + PLAYER_RADIUS;
      p.vel.x = 0;
    } else {
      if (stepUpOnto(p, b, h, world)) continue;
      p.pos.z = delta > 0 ? b.min.z - PLAYER_RADIUS : b.max.z + PLAYER_RADIUS;
      p.vel.z = 0;
    }
  }
}

export const box = (
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
): Aabb => ({
  min: { x: cx - sx / 2, y: cy - sy / 2, z: cz - sz / 2 },
  max: { x: cx + sx / 2, y: cy + sy / 2, z: cz + sz / 2 },
});
